import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Send, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import PageMeta from '@/components/PageMeta';

const API = '/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    business_name: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
    } catch (err) {
      // FastAPI 422 returns detail as an array of error objects; rendering that
      // array directly throws "Objects are not valid as a React child" and
      // blanks the page. Coerce to a friendly string.
      const detail = err.response?.data?.detail;
      setError(
        Array.isArray(detail) ? (detail[0]?.msg || 'Please check your details and try again.')
          : (typeof detail === 'string' ? detail : 'Failed to send message. Please try again.')
      );
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-2xl mx-auto px-4 py-16 sm:py-24">
          <div className="text-center" role="status">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-4">
              Message Sent!
            </h1>
            <p className="text-muted-foreground mb-8">
              Thank you for reaching out. We'll get back to you as soon as possible.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-primary hover:underline"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title="Contact" path="/contact" description="Get in touch with the OrangeLabel team — submissions, corrections, takedowns, partnerships." />
      <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-3">
            Contact Us
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Have a question or feedback? We'd love to hear from you.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Your Name *</Label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                maxLength={200}
                value={formData.name}
                onChange={handleChange}
                placeholder="John Smith"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="business_name">Business Name (Optional)</Label>
            <Input
              id="business_name"
              name="business_name"
              type="text"
              value={formData.business_name}
              onChange={handleChange}
              placeholder="Your business name"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="subject">Subject *</Label>
            <Input
              id="subject"
              name="subject"
              type="text"
              required
              maxLength={200}
              value={formData.subject}
              onChange={handleChange}
              placeholder="What's this about?"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="message">Message *</Label>
            <Textarea
              id="message"
              name="message"
              required
              maxLength={2000}
              value={formData.message}
              onChange={handleChange}
              placeholder="Your message..."
              rows={6}
              className="mt-1"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full"
            size="lg"
          >
            {submitting ? (
              <>Sending...</>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Send Message
              </>
            )}
          </Button>
        </form>

        {/* Additional Info */}
        <div className="mt-12 pt-8 border-t border-border text-center">
          <p className="text-sm text-muted-foreground">
            Want to get listed? Visit our{' '}
            <Link to="/submit" className="text-primary hover:underline">
              Get Listed
            </Link>{' '}
            page.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Contact;
